import { z } from 'zod/v4';

// Error code emitted by the ESNA measurement-upload LLFC coverage pre-flight (granular-community-back).
// When present, an MPAN is missing loss factor codes for one or more settlement dates in the file.
export const LLFC_GAP_ERROR_CODE = 'LLFC_GAP';

export const syncFileUploadErrorSchema = z.object({
  code: z.string(),
  line: z.number().nullable(),
  values: z.array(z.string()).nullable(),
});
export type SyncFileUploadError = z.infer<typeof syncFileUploadErrorSchema>;

export type FileUploadErrors = SyncFileUploadError[] | AsyncFileUploadError[];

export const getSyncUploadStatusSchema = <TReport extends z.ZodType>(report: TReport) =>
  z.object({
    errors: z.array(syncFileUploadErrorSchema).optional().nullable(),
    report: report.optional(),
  });

export type SyncUploadStatus<TReport extends z.ZodType = z.ZodAny> = z.infer<
  ReturnType<typeof getSyncUploadStatusSchema<TReport>>
>;

export const asyncFileUploadErrorSchema = z.object({
  columnName: z.string().nullable().optional(),
  columnNumber: z.number().nullable().optional(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable().optional(),
  fileContext: z.string().nullable().optional(),
  lineNumber: z.number().nullable().optional(),
});

export type AsyncFileUploadError = z.infer<typeof asyncFileUploadErrorSchema>;

export const asyncUploadStatusSchema = z.object({
  errors: z.array(asyncFileUploadErrorSchema).optional().nullable(),
  id: z.uuid(),
  processingFinished: z.boolean(),
  processingSuccessful: z.boolean().nullable(),
  status: z.string().optional(),
  uploadedAt: z.string(),
});

export type AsyncUploadStatus = z.infer<typeof asyncUploadStatusSchema>;

export type UploadStatus = AsyncUploadStatus | SyncUploadStatus;

export const orphanedMeasurements = z.object({
  exampleMeteringPoints: z.array(z.string()),
  measurementCount: z.number(),
  meteringPointCount: z.number(),
});

export type OrphanedMeasurements = z.infer<typeof orphanedMeasurements>;

export const uploadImportFileSchema = z.object({
  id: z.uuid(),
});

export type UploadImportFile = z.infer<typeof uploadImportFileSchema>;

export const multipointFileUploadRequestSchema = z.object({
  file: z.instanceof(File),
});

export type MultipointFileUploadRequest = z.infer<typeof multipointFileUploadRequestSchema>;

export const multiPointFileUploadReportSchema = z.object({
  countMeteringPoint: z.number(),
  orphanedMeasurements: orphanedMeasurements.optional().nullable(),
  periodEnd: z.string(),
  periodStart: z.string(),
  totalEnergyQuantity: z.string(),
});

export const multipointUploadStatusSchema = asyncUploadStatusSchema.extend({
  report: multiPointFileUploadReportSchema.nullable(),
});

export type MultipointUploadStatus = z.infer<typeof multipointUploadStatusSchema>;

export const demandFileUploadReportSchema = z.object({
  createdConsumerCount: z.number(),
  createdMeteringPointCount: z.number(),
  orphanedMeasurements: orphanedMeasurements.optional().nullable(),
  skippedRowCount: z.number().optional().nullable(),
  truncatedRowCount: z.number().optional().nullable(),
});

export const demandPortfolioUploadWarningSchema = z.object({
  warningCode: z.string(),
  lineNumber: z.number().optional().nullable(),
  meteringPointReference: z.string().optional().nullable(),
  suppliedStartDate: z.string().optional().nullable(),
  suppliedEndDate: z.string().optional().nullable(),
  effectiveStartDate: z.string().optional().nullable(),
  effectiveEndDate: z.string().optional().nullable(),
});

export type DemandPortfolioUploadWarning = z.infer<typeof demandPortfolioUploadWarningSchema>;

export const demandUploadStatusSchema = asyncUploadStatusSchema.extend({
  report: demandFileUploadReportSchema.nullable(),
  warnings: z.array(demandPortfolioUploadWarningSchema).optional().nullable(),
});

export type DemandUploadStatus = z.infer<typeof demandUploadStatusSchema>;

export const supplyFileUploadReportSchema = z.object({
  productionDevicesRegisteredCount: z.number(),
  productionDevicesUpdatedCount: z.number(),
  meteringPointsCreatedCount: z.number(),
});

export const supplyUploadStatusSchema = asyncUploadStatusSchema.extend({
  report: supplyFileUploadReportSchema.nullable(),
});

export type SupplyUploadStatus = z.infer<typeof supplyUploadStatusSchema>;

export const dealsFileUploadReportSchema = z.object({
  createdDealCount: z.number(),
});
export type DealsFileUploadReport = z.infer<typeof dealsFileUploadReportSchema>;

export const lesContractsFileUploadReportSchema = z.object({
  createdContractCount: z.number(),
  createdMembershipCount: z.number(),
});
export type LESContractsFileUploadReport = z.infer<typeof lesContractsFileUploadReportSchema>;

export const lossFactorCodesFileUploadReportSchema = z.object({
  affectedMpanCount: z.number(),
  affectedRowCount: z.number(),
  uploadedRowCount: z.number(),
});
export type LossFactorCodesFileUploadReport = z.infer<typeof lossFactorCodesFileUploadReportSchema>;
export const errorMap = {
  ALLOCATIONS_EXIST_FOR_CHANGED_PERIOD: 'ALLOCATIONS_EXIST_FOR_CHANGED_PERIOD',
  DUPLICATE_COLUMN_HEADERS: 'UNPARSEABLE_HEADER',
  DUPLICATE_CONFLICTING_CONSUMER: 'DUPLICATE_CONFLICTING_CONSUMER',
  EMPTY_FILE: 'NOT_ENOUGH_DATA',
  EXTRA_COLUMN: 'EXTRA_COLUMN',
  EXTRA_ROW_DATA: 'EXTRA_ROW_DATA',
  INVALID_COUNTRY: 'INVALID_COUNTRY',
  INVALID_TIME_PERIOD: 'INVALID_TIME_PERIOD',
  MEASUREMENTS_EXIST_FOR_CHANGED_PERIOD: 'MEASUREMENTS_EXIST_FOR_CHANGED_PERIOD',
  METERING_POINT_STATE_CONFLICTS_WITH_EXISTING_MEASUREMENTS:
    'METERING_POINT_STATE_CONFLICTS_WITH_EXISTING_MEASUREMENTS',
  MISSING_COLUMN: 'MISSING_COLUMN',
  MISSING_COLUMN_HEADERS: 'MISSING_COLUMN_HEADER',
  MISSING_REQUIRED_VALUE: 'type_error_none_not_allowed',
  MISSING_ROW_DATA: 'MISSING_ROW_DATA',
  NEGATIVE_QUANTITY: 'NOT_A_POSITIVE_NUMBER',
  NO_VALID_METERING_POINT_STATE_FOUND: 'NO_VALID_METERING_POINT_STATE_FOUND',
  NON_UTF8_DATA: 'NON_UTF8_DATA',
  NOT_A_NUMBER: 'NOT_A_NUMBER',
  NOT_BEGINNING_OF_DAY: 'NOT_BEGINNING_OF_DAY',
  NUMBER_TOO_LARGE: 'NOT_AN_ENERGY',
  OVERLAPPING_METERING_POINT_STATE: 'OVERLAPPING_METERING_POINT_STATE',
  TOO_MANY_COLUMNS: 'TOO_MANY_COLUMNS',
  UNKNOWN_REFERENCE: 'UNKNOWN_REFERENCE',
  UNPARSEABLE_DATETIME: 'UNPARSEABLE_DATETIME',
  UNPARSEABLE_FILE: 'UNPARSEABLE_FILE',
  UNRECOGNISED_COLUMN_HEADERS: 'UNPARSEABLE_HEADER',
  UNSUPPORTED_DATETIME: 'UNSUPPORTED_DATETIME',
  UNZONED_DATETIME: 'NOT_ZONED_DATE_TIME',
};

export type ErrorMapKey = keyof typeof errorMap;
